import { useCallback } from 'react';

import { createActionsHook, createHook } from 'react-sweet-state';

import {
	SUGGESTED_PROMPT_TILE_STREAM_MESSAGE_TYPE,
	type SuggestedPromptTile,
	type SuggestedPromptTileStreamMessage,
} from '@atlassian/conversation-assistant-service-api/types/assistance-service';
import {
	type AssistanceServiceInjectedParams,
	type AssistanceServiceInputParam,
	createHookWithAssistanceService,
} from '@atlassian/conversation-assistant-service/services/assistance-service/main';

import { getPrimaryPrompts } from './getPrimaryPrompts';
import { getSecondaryPrompts } from './getSecondaryPrompts';
import { PRIMARY_PROMPTS_COUNT } from './PRIMARY_PROMPTS_COUNT';
import { PROMPT_SUGGESTIONS_OFFSET } from './PROMPT_SUGGESTIONS_OFFSET';
import { SECONDARY_PROMPTS_COUNT } from './SECONDARY_PROMPTS_COUNT';
import { store } from './store';
import type { UsePromptSuggestionsReturn } from './types';
import { useThirdPartySuggestedPromptTile } from './useThirdPartySuggestedPromptTile';

const TOTAL_PROMPT_SUGGESTIONS_COUNT = PRIMARY_PROMPTS_COUNT + SECONDARY_PROMPTS_COUNT;

const parsePromptSuggestionsStream = async ({
	generator,
	onPrimaryPromptsReady,
	onSecondaryPromptsReady,
}: {
	generator: AsyncGenerator<SuggestedPromptTileStreamMessage>;
	onPrimaryPromptsReady: (tiles: SuggestedPromptTile[]) => void;
	onSecondaryPromptsReady: (tiles: SuggestedPromptTile[]) => void;
}): Promise<SuggestedPromptTile[]> => {
	const tiles: SuggestedPromptTile[] = [];

	try {
		for await (const chunk of generator) {
			if (chunk.type !== SUGGESTED_PROMPT_TILE_STREAM_MESSAGE_TYPE) {
				continue;
			}

			tiles.push(chunk.message);

			if (tiles.length === PRIMARY_PROMPTS_COUNT) {
				onPrimaryPromptsReady(getPrimaryPrompts(tiles));
			}

			if (tiles.length === TOTAL_PROMPT_SUGGESTIONS_COUNT) {
				onSecondaryPromptsReady(getSecondaryPrompts(tiles));
				break;
			}
		}
	} catch (error) {
		if (tiles.length === 0) {
			throw error;
		}
	} finally {
		// If stream returns < TOTAL_PROMPT_SUGGESTIONS_COUNT (on either success or partial failure), return whatever we have
		if (tiles.length < PRIMARY_PROMPTS_COUNT) {
			onPrimaryPromptsReady(getPrimaryPrompts(tiles));
		}
		if (tiles.length < TOTAL_PROMPT_SUGGESTIONS_COUNT) {
			onSecondaryPromptsReady(getSecondaryPrompts(tiles));
		}
	}

	return tiles.slice(0, TOTAL_PROMPT_SUGGESTIONS_COUNT);
};

const useStore = createHook(store);

const useStoreActions = createActionsHook(store);

export const usePromptSuggestions: (
	props: AssistanceServiceInputParam,
) => UsePromptSuggestionsReturn = createHookWithAssistanceService(
	({ assistanceServiceInjectedParams }: AssistanceServiceInjectedParams) => {
		const { assistanceService, touchpointSource, product, experienceId, siteId, headers, baseUrl } =
			assistanceServiceInjectedParams;
		const [state] = useStore();
		const {
			getState,
			setLoading,
			setPrimaryPrompts,
			setSecondaryPrompts,
			setData,
			shuffleSuggestions,
			setError,
		} = useStoreActions();

		const { enable3pPromptOn3rdCard, ready: is3pReady } = useThirdPartySuggestedPromptTile({
			assistanceServiceParams: {
				touchpointSource,
				product,
				experienceId,
				siteId,
				headers,
				baseUrl,
			},
			tilesDisplayed: (state.primaryPrompts?.length ?? 0) > 0,
		});

		const loadSuggestions = useCallback(async () => {
			// Wait for the 3P signal to settle so we request a stable flag value (always
			// ready off a 3P surface); the host effect re-fires this when `is3pReady` flips.
			if (!is3pReady) {
				return;
			}

			const { isLoading, data, error, primaryPrompts, secondaryPrompts } = getState();
			if (isLoading || data || error || primaryPrompts || secondaryPrompts) {
				return;
			}

			setLoading();

			try {
				const { generator } = await assistanceService.fetchPersonalizedPromptSuggestionsStream({
					offset: PROMPT_SUGGESTIONS_OFFSET,
					limit: TOTAL_PROMPT_SUGGESTIONS_COUNT,
					enable3pPromptOn3rdCard,
				});

				const prompts = await parsePromptSuggestionsStream({
					generator,
					onPrimaryPromptsReady: setPrimaryPrompts,
					onSecondaryPromptsReady: setSecondaryPrompts,
				});

				if (prompts.length === 0) {
					throw new Error('No prompt suggestions returned');
				}
				setData(prompts);
			} catch (err: unknown) {
				setError(err instanceof Error ? err : new Error('Failed to fetch prompt suggestions'));
			}
		}, [
			assistanceService,
			getState,
			setLoading,
			setPrimaryPrompts,
			setSecondaryPrompts,
			setData,
			setError,
			is3pReady,
			enable3pPromptOn3rdCard,
		]);

		return {
			...state,
			loadSuggestions,
			shuffleSuggestions,
		};
	},
);
